'use client';

import { TravelReviewCGType } from '@/types/graphql/root';
import MoreDropdownButton from './more-dropdown-button';
import { getDisplayCreatedAt } from '@/utils/date-calc';
import { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useSetAtom } from 'jotai';
import { blackTransparentLoaderModalAtom, centerToastModalAtom, confirmModalAtom } from '@/atom/modal';
import { deleteTravelReview, signinWithZzimToken } from '@/utils/actions';
import SharingTripReportModal from './sharing-trip-report-modal';
import dayjs from 'dayjs';

export default function SharingTripPostTitleContainer({ post }: { post: TravelReviewCGType }) {
  const router = useRouter();

  const setConfirmModal = useSetAtom(confirmModalAtom);
  const setBlackTransparentLoaderModal = useSetAtom(blackTransparentLoaderModalAtom);
  const setCenterToastModal = useSetAtom(centerToastModalAtom);

  const [isOpenReportModal, setIsOpenReportModal] = useState(false);

  const category = useMemo(() => {
    if (post.category === 'AFTER_TRAVEL') return '여행 후기';
    else if (post.category === 'BEFORE_TRAVEL') return '여행 예정';
    return null;
  }, [post.category]);

  const onClickDeletePost = useCallback(() => {
    const deletePost = async () => {
      try {
        setBlackTransparentLoaderModal(true);
        await deleteTravelReview(post.id);
        router.refresh();
      } catch (error) {
        setCenterToastModal({
          text: '게시글 삭제에 실패했습니다.',
          renderingTime: 1000,
        });
      } finally {
        setBlackTransparentLoaderModal(false);
      }
    };

    setConfirmModal({
      visible: true,
      title: '게시글 삭제',
      message: '게시글을 삭제하시겠습니까?',
      onConfirm: () => {
        setConfirmModal({ visible: false });
        deletePost();
      },
      onClose: () => {
        setConfirmModal({ visible: false });
      },
      onOuterClick: () => {
        setConfirmModal({ visible: false });
      },
    });
  }, []);

  const onClickReport = useCallback(async () => {
    const signinResult = await signinWithZzimToken();
    if (!signinResult) {
      setConfirmModal({
        visible: true,
        title: '로그인 필요',
        message: '로그인으로 이동합니다.',
        onClose: () => {
          setConfirmModal({ visible: false });
        },
        onConfirm: () => {
          setConfirmModal({ visible: false });
          window.location.href =
            process.env.NEXT_PUBLIC_ZZIMCAR_URL + 'user/signin?redirectUrl=' + window.location.href;
        },
      });
      return;
    }
    setIsOpenReportModal(true);
  }, []);

  return (
    <>
      <div className='flex flex-col gap-8pxr px-20pxr py-16pxr'>
        <div className='flex items-start justify-between gap-8pxr'>
          <h3 className='body-16-regular text-new-gray-900'>{post.title}</h3>
          {post.isOwner && (
            <MoreDropdownButton>
              <div
                className='absolute z-[2] right-0 top-30pxr border border-new-gray-300 rounded-8pxr flex flex-col bg-white overflow-hidden'
                style={{
                  boxShadow: '0px 4px 8px 0px rgba(14, 31, 53, 0.12)',
                }}
              >
                <div
                  className='py-8pxr px-16pxr body-14-regular text-new-gray-900 whitespace-nowrap cursor-pointer hover:bg-new-point-50'
                  onClick={() => {
                    router.push(`/board/sharing-trip-write/${post.id}`);
                  }}
                >
                  수정하기
                </div>
                <div
                  className='py-8pxr px-16pxr body-14-regular text-new-gray-900 whitespace-nowrap cursor-pointer hover:bg-new-point-50'
                  onClick={() => {
                    onClickDeletePost();
                  }}
                >
                  삭제하기
                </div>
              </div>
            </MoreDropdownButton>
          )}
          {!post.isOwner && (
            <MoreDropdownButton>
              <div
                className='absolute z-[2] right-0 top-30pxr border border-new-gray-300 rounded-8pxr flex flex-col bg-white overflow-hidden'
                style={{
                  boxShadow: '0px 4px 8px 0px rgba(14, 31, 53, 0.12)',
                }}
              >
                <div
                  className='py-8pxr px-16pxr body-14-regular text-new-gray-900 whitespace-nowrap cursor-pointer hover:bg-new-point-50'
                  onClick={() => {
                    onClickReport();
                  }}
                >
                  신고하기
                </div>
              </div>
            </MoreDropdownButton>
          )}
        </div>

        <div className='flex items-center justify-start gap-8pxr flex-wrap'>
          <div className='body-14-regular text-new-gray-900'>{post.userName}</div>
          <div className='body-12-regular text-new-gray-700'>{category}</div>
          <div className='w-1pxr h-16pxr bg-new-gray-300'></div>
          <div className='body-12-regular text-new-gray-700'>{dayjs(post.createdAt).format('YYYY.MM.DD')}</div>
          <div className='w-1pxr h-16pxr bg-new-gray-300'></div>
          <div className='body-12-regular text-new-gray-700'>{post.viewCount} 조회</div>
        </div>

        <div className='w-full h-1pxr bg-new-gray-200 mt-4pxr'></div>
      </div>
      <SharingTripReportModal
        visible={isOpenReportModal}
        setVisible={setIsOpenReportModal}
        reviewId={post.id}
        commentId={null}
      />
    </>
  );
}
